import { Timestamp } from 'firebase/firestore';

export const MAIN_EVENTER_DURATION_DAYS = 14;
export const MAIN_EVENTER_COOLDOWN_DAYS = 14;
export const MIN_MAIN_EVENTER_DAYS = 1;
export const MAX_MAIN_EVENTER_DAYS = 30;

export const MAIN_EVENTER_TIMING_PRESETS = [
  {
    id: 'sprint',
    label: 'Sprint',
    durationDays: 1,
    cooldownDays: 3,
  },
  {
    id: 'weekly',
    label: 'Settimanale',
    durationDays: 7,
    cooldownDays: 7,
  },
  {
    id: 'default',
    label: 'Standard',
    durationDays: MAIN_EVENTER_DURATION_DAYS,
    cooldownDays: MAIN_EVENTER_COOLDOWN_DAYS,
  },
  {
    id: 'extended',
    label: 'Esteso',
    durationDays: 21,
    cooldownDays: 21,
  },
  {
    id: 'monthly',
    label: 'Mensile',
    durationDays: 30,
    cooldownDays: 28,
  },
];

export const clampMainEventerTimingDays = (
  value,
  fallback = MAIN_EVENTER_DURATION_DAYS
) => {
  const parsedValue = Number(value);

  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }

  return Math.min(
    MAX_MAIN_EVENTER_DAYS,
    Math.max(MIN_MAIN_EVENTER_DAYS, Math.round(parsedValue))
  );
};

export const getMainEventerDurationDays = (source = {}) =>
  clampMainEventerTimingDays(
    source?.mainEventerDurationDays,
    MAIN_EVENTER_DURATION_DAYS
  );

export const getMainEventerCooldownDays = (source = {}) =>
  clampMainEventerTimingDays(
    source?.mainEventerCooldownDays,
    MAIN_EVENTER_COOLDOWN_DAYS
  );

export const formatMainEventerDaysLabel = (value) => {
  const days = clampMainEventerTimingDays(value);

  if (days === 1) return '1 giorno';
  if (days === 7) return '1 settimana';
  if (days === 14) return '2 settimane';
  if (days === 21) return '3 settimane';
  if (days === 28) return '4 settimane';
  if (days === 30) return '1 mese';

  return `${days} giorni`;
};

export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const startOfDay = (date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

export const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

export const buildMainEventerCooldownUntil = (
  removedAt = new Date(),
  cooldownDays = MAIN_EVENTER_COOLDOWN_DAYS
) =>
  addDays(
    startOfDay(toDate(removedAt) || new Date()),
    clampMainEventerTimingDays(cooldownDays, MAIN_EVENTER_COOLDOWN_DAYS)
  );

const getCooldownValue = (value) => {
  if (!value || value instanceof Date || typeof value?.toDate === 'function' || typeof value?.seconds === 'number') {
    return value;
  }

  if (typeof value === 'object') {
    return value.availableAt || value.until || value.expiresAt || value.cooldownUntil || value.blockedUntil;
  }

  return value;
};

export const normalizeMainEventerCooldowns = (source = {}, referenceDate = new Date()) => {
  const reference = toDate(referenceDate) || new Date();
  const entries = Array.isArray(source)
    ? source.map((item) => [
        item?.wrestlerId || item?.id,
        getCooldownValue(item),
      ])
    : Object.entries(source || {}).map(([wrestlerId, value]) => [
        wrestlerId,
        getCooldownValue(value),
      ]);

  return entries.reduce((acc, [wrestlerId, value]) => {
    const cooldownUntil = toDate(value);

    if (wrestlerId && cooldownUntil && cooldownUntil.getTime() > reference.getTime()) {
      acc[wrestlerId] = cooldownUntil;
    }

    return acc;
  }, {});
};

export const getActiveMainEventerCooldown = (
  cooldowns = {},
  wrestlerId,
  referenceDate = new Date()
) => {
  if (!wrestlerId) return null;

  const reference = toDate(referenceDate) || new Date();
  const cooldownUntil = toDate(cooldowns[wrestlerId]);

  return cooldownUntil && cooldownUntil.getTime() > reference.getTime()
    ? cooldownUntil
    : null;
};

export const withMainEventerCooldown = (
  cooldowns = {},
  wrestlerId,
  removedAt = new Date(),
  referenceDate = new Date(),
  cooldownDays = MAIN_EVENTER_COOLDOWN_DAYS
) => {
  if (!wrestlerId) {
    return normalizeMainEventerCooldowns(cooldowns, referenceDate);
  }

  const cooldownUntil = buildMainEventerCooldownUntil(removedAt, cooldownDays);
  const nextCooldowns = normalizeMainEventerCooldowns(cooldowns, referenceDate);
  const existingCooldownUntil = toDate(nextCooldowns[wrestlerId]);

  nextCooldowns[wrestlerId] =
    existingCooldownUntil && existingCooldownUntil.getTime() > cooldownUntil.getTime()
      ? existingCooldownUntil
      : cooldownUntil;

  return normalizeMainEventerCooldowns(nextCooldowns, referenceDate);
};

export const serializeMainEventerCooldowns = (
  cooldowns = {},
  referenceDate = new Date()
) =>
  Object.entries(normalizeMainEventerCooldowns(cooldowns, referenceDate)).reduce(
    (acc, [wrestlerId, cooldownUntil]) => {
      acc[wrestlerId] = Timestamp.fromDate(startOfDay(cooldownUntil));
      return acc;
    },
    {}
  );
